import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TENANT_CONST } from '@/constant';
import { Add, Remove } from '@/package';
import { VFlex, VIconButton, cn } from '@/package';
import type { Tenant } from '@/type';

type PlanItemProps = {
  initBillingUnit?: number;
  isCompleted?: boolean;
  isSelected?: boolean;
  tier: Tenant['tier'];
  onBillingUnitChange?(billingUnit: Tenant['billingUnit']): void;
  onTierClick?(tier: Tenant['tier']): void;
};

export const PlanItem = ({
  initBillingUnit,
  isCompleted,
  isSelected,
  tier,
  onTierClick,
  onBillingUnitChange,
}: PlanItemProps) => {
  const { t } = useTranslation();
  const [billingUnit, setBillingUnit] = useState(initBillingUnit ?? 1);
  const plan = TENANT_CONST.PLAN_BY_TIER[tier];
  const isSubtractable =
    tier === TENANT_CONST.TIER.STANDARD && billingUnit >= 2;
  const isAddable = tier === TENANT_CONST.TIER.STANDARD;

  useEffect(() => {
    onBillingUnitChange?.(billingUnit);
  }, [billingUnit]);

  return (
    <div
      className={cn(
        'h-[326px] w-[276px] cursor-pointer rounded-lg border p-6 text-black',
        {
          'border-gray-400': !isSelected,
          'bg-gray-100': !isSelected,
          'border-primary-400': isSelected,
          'bg-skyBlue-200': isSelected,
        },
      )}
      onClick={() => onTierClick?.(tier)}
    >
      <VFlex direction="col" className="gap-6">
        <VFlex direction="col" className="gap-1">
          <div className="text-headline-sm leading-6 font-bold ">
            {t(plan.name)}
          </div>
          {!isCompleted ? (
            <VFlex items="center" justify="between">
              <div className="text-body-md leading-none font-normal">
                {t('pricing.seat.nonemendation')}
              </div>

              <VFlex justify="end">
                <VFlex className="px-1" items="center" gap={0.25}>
                  <VIconButton
                    Icon={Remove}
                    size="md"
                    variant="roundSecondary"
                    onClick={() => setBillingUnit(v => v - 1)}
                    disabled={!isSubtractable}
                  />
                  <VFlex
                    className="h-[24px] w-[29px] select-none"
                    justify="center"
                    items="center"
                  >
                    <div>{billingUnit}</div>
                  </VFlex>
                  <VIconButton
                    Icon={Add}
                    size="md"
                    variant="roundSecondary"
                    onClick={() => setBillingUnit(v => v + 1)}
                    disabled={!isAddable}
                  />
                </VFlex>
              </VFlex>
            </VFlex>
          ) : (
            <VFlex items="center" gap={0.5}>
              <div className="translate-y-[1px] text-body-md">
                {t('pricing.totalSeats.subintegumental')}
              </div>

              <div className="rounded-sm bg-white px-2 text-body-md font-bold select-none">
                {billingUnit}
              </div>
            </VFlex>
          )}
        </VFlex>
        <VFlex
          direction="col"
          className="text-body-lg leading-none font-normal"
        >
          {plan.description.map(desc => (
            <div className="leading-normal" key={desc}>
              • {t(desc)}
            </div>
          ))}
        </VFlex>
      </VFlex>
    </div>
  );
};

PlanItem.displayName = 'PlanItem';
